import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import SignatureCanvas from "react-signature-canvas";
import { Eraser, Check, X, Loader2, FileSignature, FileText } from "lucide-react";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_client-360-pro/artifacts/t5motboh_wertia%20perfect.png";

/** Public mobile signature page (no auth) — opened by scanning QR code
 *  OR by clicking the link inside a WertiaBox email (purpose="document_signature").
 *  Route: /sign/:sessionId  or  /m/sign/:sessionId
 */
export default function MobileSignaturePage() {
  const { sessionId } = useParams();
  const sigRef = useRef(null);
  const canvasWrapRef = useRef(null);
  const [canvasSize, setCanvasSize] = useState({ w: 600, h: 280 });
  const [status, setStatus] = useState("idle"); // idle | sending | success | error | expired
  const [drawn, setDrawn] = useState(false);
  const [errMsg, setErrMsg] = useState("");
  const [signInfo, setSignInfo] = useState(null); // { doc_type, doc_type_label, client_name, ... }

  const apiBase = process.env.REACT_APP_BACKEND_URL;

  // ── Resize the canvas to match container width (high-DPI aware) ──────────
  // Without this, react-signature-canvas defaults to 300×150 internal pixels
  // while CSS stretches to container width → strokes look stretched/blurry.
  useLayoutEffect(() => {
    const resize = () => {
      const wrap = canvasWrapRef.current;
      if (!wrap) return;
      const rect = wrap.getBoundingClientRect();
      const isPortrait = window.innerHeight > window.innerWidth;
      const h = isPortrait ? Math.min(360, Math.max(220, Math.round(rect.width * 0.55))) : 260;
      setCanvasSize({ w: Math.round(rect.width), h });
    };
    resize();
    window.addEventListener("resize", resize);
    window.addEventListener("orientationchange", resize);
    return () => {
      window.removeEventListener("resize", resize);
      window.removeEventListener("orientationchange", resize);
    };
  }, []);

  // ── Apply device-pixel-ratio for sharp strokes on retina ──────────
  // After react-signature-canvas mounts, increase the canvas backing store
  // resolution and scale the drawing context so strokes look crisp.
  useEffect(() => {
    const sig = sigRef.current;
    if (!sig) return;
    const canvas = sig.getCanvas?.();
    if (!canvas) return;
    const dpr = Math.min(window.devicePixelRatio || 1, 3);
    if (dpr <= 1) return;
    const cssW = canvasSize.w;
    const cssH = canvasSize.h;
    canvas.width = Math.round(cssW * dpr);
    canvas.height = Math.round(cssH * dpr);
    canvas.style.width = `${cssW}px`;
    canvas.style.height = `${cssH}px`;
    const ctx = canvas.getContext("2d");
    if (ctx) ctx.scale(dpr, dpr);
    // Clear any pre-existing strokes (they'd render at wrong scale)
    try { sig.clear(); } catch { /* */ }
  }, [canvasSize]);

  // Resolve session purpose:
  //  - document_signature → has /sign/{token}/info + PDF preview
  //  - identity / etc. → fall back to existing m/upload-sessions metadata
  useEffect(() => {
    (async () => {
      try {
        const r = await axios.get(`${apiBase}/api/sign/${sessionId}/info`);
        if (r.data?.status === "completed" || r.data?.status === "uploaded") {
          setStatus("success");
        }
        setSignInfo(r.data);
      } catch {
        // Not a signature-request session → try the legacy upload-session
        try {
          const res = await axios.get(`${apiBase}/api/m/upload-sessions/${sessionId}`);
          if (res.data.status === "uploaded") setStatus("success");
        } catch {
          setStatus("expired");
          setErrMsg("Sitzung ungültig oder abgelaufen");
        }
      }
    })();
  }, [apiBase, sessionId]);

  const clear = () => { sigRef.current?.clear(); setDrawn(false); };

  const submit = async () => {
    if (!sigRef.current || sigRef.current.isEmpty()) return;
    setStatus("sending");
    try {
      const dataUrl = sigRef.current.toDataURL();
      await axios.post(
        `${apiBase}/api/m/upload-sessions/${sessionId}/signature`,
        { signature: dataUrl },
      );
      // If this is a document-signature request, finalize the PDF on the server.
      if (signInfo) {
        try {
          await axios.post(`${apiBase}/api/sign/${sessionId}/finalize`);
        } catch (e) {
          // Already finalized or other error — still consider it successful for the user
          // eslint-disable-next-line no-console
          console.warn("finalize call failed:", e?.response?.data);
        }
      }
      setStatus("success");
    } catch (e) {
      setStatus("error");
      setErrMsg(e.response?.data?.detail || "Fehler beim Senden der Unterschrift");
    }
  };

  const isDocSignature = !!signInfo;
  // Doc Designer signatures have no server-side PDF — we just show the
  // template name + signer pad on the public page.
  const hasPdfPreview = isDocSignature && signInfo?.purpose !== "doc_designer_signature";
  const pdfUrl = hasPdfPreview ? `${apiBase}/api/sign/${sessionId}/preview-pdf` : null;

  return (
    <div className="min-h-screen flex flex-col" style={{ background: "linear-gradient(180deg, #15100e 0%, #2E2622 100%)", color: "#F9F8F6" }} data-testid="mobile-signature-page">
      {/* Header — Wertia branded */}
      <div className="px-5 py-5 border-b border-prime/20 flex flex-col items-center" style={{ background: "rgba(0,0,0,.25)" }}>
        <img src={LOGO_URL} alt="Wertia Finanz" className="h-14" />
        <div className="mt-2 text-[10px] uppercase tracking-[.3em] text-prime font-semibold">WERTIA · FINANZ</div>
        <div className="text-[10px] text-white/40 mt-1">Vermögensberatung · Zürich</div>
      </div>

      <div className="flex-1 px-5 py-6 flex flex-col">
        {status === "success" ? (
          <div className="flex-1 flex flex-col items-center justify-center text-center gap-4">
            <div className="w-24 h-24 rounded-full flex items-center justify-center shadow-xl" style={{ background: "linear-gradient(135deg, #C89F50, #A37A33)" }}>
              <Check className="w-12 h-12" strokeWidth={2.5} style={{ color: "#15100e" }} />
            </div>
            <h2 className="font-serif text-3xl font-bold text-prime">Unterschrift erhalten</h2>
            <p className="text-white/70 max-w-xs leading-relaxed">
              Vielen Dank! Ihre Unterschrift wurde sicher übermittelt. Sie können diese Seite nun schliessen.
            </p>
            {isDocSignature && (
              <p className="text-white/40 text-xs mt-2 max-w-xs leading-relaxed">
                Ihr unterzeichnetes Dokument wird automatisch in Ihrem Kundendossier abgelegt.
              </p>
            )}
            <div className="mt-6 inline-flex items-center gap-1.5 text-[10px] uppercase tracking-widest text-prime/80">
              <span className="w-8 h-px bg-prime/40" /> Wertia Finanz GmbH <span className="w-8 h-px bg-prime/40" />
            </div>
          </div>
        ) : status === "expired" || status === "error" ? (
          <div className="flex-1 flex flex-col items-center justify-center text-center gap-4">
            <div className="w-20 h-20 rounded-full bg-red-600 flex items-center justify-center">
              <X className="w-10 h-10" strokeWidth={2.5} />
            </div>
            <h2 className="font-serif text-2xl font-bold">Fehler</h2>
            <p className="text-white/70 max-w-xs">{errMsg}</p>
          </div>
        ) : (
          <>
            {/* Document context (for /sign/{token} purpose=document_signature OR antrag_signature) */}
            {isDocSignature && (
              <div className="mb-4 rounded-xl p-4 shadow-lg" style={{ background: "rgba(255,255,255,.05)", border: "1px solid rgba(200,159,80,.3)" }} data-testid="sign-doc-context">
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 rounded-lg flex items-center justify-center shrink-0" style={{ background: "rgba(200,159,80,.15)" }}>
                    <FileText className="w-5 h-5 text-prime" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-[10px] uppercase tracking-[.2em] text-prime/80">Zu unterschreiben</div>
                    <div className="font-serif text-lg font-semibold mt-0.5 text-white">{signInfo.doc_type_label}</div>
                    <div className="text-xs text-white/70 mt-1">
                      Für: <span className="text-white font-medium">{signInfo.client_name}</span>
                    </div>
                    {signInfo.consultant_name && (
                      <div className="text-[10px] text-white/40 mt-0.5">Gesendet von: {signInfo.consultant_name}</div>
                    )}
                  </div>
                </div>
                {pdfUrl && (
                  <a
                    href={pdfUrl}
                    target="_blank" rel="noopener noreferrer"
                    className="mt-3 inline-flex items-center gap-1.5 text-xs text-prime hover:text-prime-light transition-colors border-b border-prime/30 hover:border-prime"
                    data-testid="sign-preview-pdf"
                  >
                    <FileText className="w-3.5 h-3.5" /> Dokument als PDF anzeigen
                  </a>
                )}
              </div>
            )}

            <div className="text-center mb-5">
              <div className="text-xs uppercase tracking-[.25em] text-prime font-bold flex items-center justify-center gap-1.5">
                <FileSignature className="w-3.5 h-3.5" /> Digitale Unterschrift
              </div>
              <h1 className="font-serif text-2xl font-bold mt-1.5 text-white">Bitte hier unterschreiben</h1>
              <p className="text-xs text-white/60 mt-1.5 max-w-sm mx-auto leading-relaxed">
                Verwenden Sie Finger oder Eingabestift. Ihre Unterschrift wird sicher übermittelt.
              </p>
              <div className="mt-3 flex items-center justify-center gap-2">
                <span className="w-12 h-px bg-prime/30" />
                <span className="w-1.5 h-1.5 rounded-full bg-prime" />
                <span className="w-12 h-px bg-prime/30" />
              </div>
            </div>

            <div ref={canvasWrapRef} className="bg-white rounded-xl p-2 relative shadow-2xl" style={{ boxShadow: "0 8px 32px rgba(200,159,80,.25), 0 0 0 1px rgba(200,159,80,.4)" }}>
              {/* Baseline indicator (signature line) */}
              <div className="absolute left-6 right-6 bottom-12 border-b border-dashed border-stone-300 pointer-events-none" />
              <div className="absolute left-6 bottom-3 text-[10px] uppercase tracking-wider text-stone-400 pointer-events-none">
                Unterschrift
              </div>
              <SignatureCanvas
                ref={sigRef}
                penColor="#0033A0"
                minWidth={1.2}
                maxWidth={3.2}
                velocityFilterWeight={0.7}
                throttle={8}
                onEnd={() => setDrawn(sigRef.current && !sigRef.current.isEmpty())}
                canvasProps={{
                  className: "bg-white rounded touch-none",
                  width: canvasSize.w,
                  height: canvasSize.h,
                  style: {
                    width: `${canvasSize.w}px`,
                    height: `${canvasSize.h}px`,
                    touchAction: "none",
                    display: "block",
                  },
                  "data-testid": "mobile-sig-canvas",
                }}
              />
              {!drawn && (
                <span className="absolute inset-0 flex items-center justify-center pointer-events-none text-stone-300 italic text-sm">
                  Hier mit dem Finger oder Stift unterschreiben
                </span>
              )}
              <button
                onClick={clear}
                className="absolute top-2 right-2 w-9 h-9 bg-white/95 backdrop-blur border border-stone-200 rounded-full flex items-center justify-center text-stone-500 hover:bg-red-600 hover:text-white shadow-md"
                data-testid="mobile-sig-clear"
                title="Löschen"
              >
                <Eraser className="w-4 h-4" />
              </button>
            </div>

            <div className="flex gap-3 mt-5">
              <button
                onClick={clear} disabled={!drawn || status === "sending"}
                className="flex-1 py-3.5 border border-prime/30 text-prime hover:bg-prime/10 rounded-lg text-sm font-semibold uppercase tracking-wider disabled:opacity-40 transition-colors"
                data-testid="mobile-sig-reset-btn"
              >
                Löschen
              </button>
              <button
                onClick={submit} disabled={!drawn || status === "sending"}
                className="flex-1 py-3.5 rounded-lg text-sm font-bold uppercase tracking-wider disabled:opacity-40 flex items-center justify-center gap-2 shadow-lg transition-all"
                style={{
                  background: drawn ? "linear-gradient(135deg, #DFB864, #C89F50)" : "rgba(200,159,80,.3)",
                  color: "#15100e",
                  boxShadow: drawn ? "0 4px 16px rgba(200,159,80,.4)" : "none",
                }}
                data-testid="mobile-sig-send-btn"
              >
                {status === "sending"
                  ? <><Loader2 className="w-4 h-4 animate-spin" /> Senden…</>
                  : <><Check className="w-4 h-4" /> Unterschrift senden</>}
              </button>
            </div>
          </>
        )}
      </div>

      {/* Footer — Wertia branded */}
      <div className="px-5 py-4 border-t border-prime/15 text-center" style={{ background: "rgba(0,0,0,.35)" }}>
        <div className="text-[9px] uppercase tracking-[.3em] text-prime/70">Wertia Finanz GmbH</div>
        <div className="text-[10px] text-white/40 mt-1">
          Altstetterstrasse 124 · 8048 Zürich · <span className="text-prime/60">www.wertiafinanz.ch</span>
        </div>
        <div className="text-[9px] text-white/30 mt-1.5 flex items-center justify-center gap-1.5">
          <span>🔒 SSL gesichert</span><span>·</span><span>FINMA-ready</span>
        </div>
      </div>
    </div>
  );
}
